import React, { createContext, useContext, useState, useEffect, useMemo } from "react";

const DICT = {
  fr: {
    nav: {
      directory: "Annuaire",
      partner: "Passez partenaire",
      login: "Connexion",
      dashboard: "Espace partenaire",
      admin: "Admin",
      logout: "Déconnexion",
      search_placeholder: "Rechercher une entreprise…",
    },
    home: {
      kicker: "L'annuaire premium du Haut-Rhin",
      h1: "Passez partenaire pour activer les outils qui transforment votre visibilité locale en opportunités clients.",
      sub: "Les habitants du 68 — et au-delà des frontières — recherchent leurs commerces de proximité avec une IA conçue pour le local. Soyez là quand ils décident.",
      search_placeholder: "Que cherchez-vous ? « plombier urgent à Mulhouse », « boulangerie ouverte »…",
      search_cta: "Rechercher",
      smart_label: "Recherche IA",
      proof_users: "Visiteurs locaux",
      proof_searches: "Recherches effectuées",
      proof_premium: "Commerces partenaires",
      proof_cities: "Communes du 68",
      proof_total: "Entreprises référencées",
      partner_kicker: "Pour les commerçants",
      partner_h2: "Une fiche premium qui travaille pour vous, jour et nuit",
      partner_lead: "Visibilité prioritaire, contact direct, IA dédiée. Sans engagement.",
      cta_partner: "Découvrir l'offre partenaire",
      cta_directory: "Parcourir l'annuaire",
      featured_kicker: "Mis en avant",
      featured_h2: "Nos commerçants partenaires",
      shopkeeper_kicker: "Vous êtes commerçant ?",
      shopkeeper_h2: "Votre entreprise est peut-être déjà référencée",
      shopkeeper_lead: "Nous avons déjà référencé plus de {count} entreprises du 68 à partir des données publiques. Trouvez la vôtre en quelques secondes, réclamez-la, et mettez-la en avant.",
      shopkeeper_find: "Trouver mon entreprise",
      shopkeeper_search_placeholder: "Nom de votre entreprise…",
      shopkeeper_steps_title: "3 étapes, c'est tout",
      shopkeeper_step1: "Cherchez le nom de votre entreprise ci-dessus",
      shopkeeper_step2: "Cliquez sur « C'est mon entreprise » pour la réclamer",
      shopkeeper_step3: "Activez votre profil partenaire pour passer en tête d'affiche",
    },
    directory: {
      title: "Annuaire des entreprises du Haut-Rhin",
      filter_city: "Ville",
      filter_category: "Catégorie",
      filter_keyword: "Mot-clé",
      filter_keyword_placeholder: "Nom, ville…",
      all_option: "— Toutes —",
      results_label: "entreprises",
      premium_only: "Partenaires premium uniquement",
      premium_only_chip: "Premium uniquement",
      premium_new_week: "+{n} cette semaine",
      premium_new_title: "{n} nouvelles entreprises Premium cette semaine sur {total} au total",
      sort_label: "Tri :",
      sort_name_asc: "Nom A → Z",
      sort_name_desc: "Nom Z → A",
      sort_city_asc: "Ville A → Z",
      sort_postal_asc: "Code postal ↑",
      sort_premium_first: "Premium d'abord",
      sort_rating: "Meilleures notes",
      sort_relevance: "Pertinence",
      view_list: "Liste",
      view_map: "Carte",
      loading: "Chargement…",
      empty: "Aucun résultat. L'annuaire continue son import — revenez bientôt.",
      premium_badge: "Partenaire",
      view: "Voir la fiche",
      eyebrow: "Annuaire 68",
    },
    business: {
      claim: "Réclamer cette fiche",
      contact_form: "Envoyer un message",
      reviews: "Avis clients",
      submit_review: "Laisser un avis",
      no_reviews: "Aucun avis approuvé pour le moment.",
      premium_only_contact: "Coordonnées disponibles via la fiche partenaire — devenez partenaire pour activer le clic-pour-appeler et WhatsApp.",
      website: "Site web",
      send_message: "Envoyer",
      name: "Nom",
      email: "Email",
      phone: "Téléphone",
      message: "Votre message",
      sent: "Message envoyé. Vous serez recontacté rapidement.",
    },
    pricing: {
      kicker: "Sans engagement, résiliable à tout moment",
      h1: "Activez les outils qui convertissent votre visibilité locale",
      premium_name: "Premium",
      premium_ai_name: "Premium IA",
      per_month: "/ mois",
      per_year: "/ an",
      billing_monthly: "Mensuel",
      billing_annual: "Annuel",
      billing_save: "-17%",
      promo_banner: "-50 % la 1re année pour tout nouvel abonnement",
      promo_first_year: "-50 % la 1re année",
      promo_first_months: "-50 % les 12 premiers mois",
      price_then: "Puis",
      equivalent_monthly: "soit {price} / mois",
      start_trial_cta: "Démarrer mon essai gratuit",
      cta: "Devenir partenaire",
      most_popular: "POPULAIRE",
      features_premium: [
        "Visibilité prioritaire dans l'annuaire",
        "Badge Partenaire",
        "Bouton appel direct",
        "Bouton WhatsApp",
        "Statistiques mensuelles",
        "Support email prioritaire",
      ],
      features_premium_ai: [
        "Tout de Premium",
        "Générateur de contenu IA",
        "Assistant prix indicatif",
        "Réponses IA aux avis",
        "Conseiller visibilité IA",
        "Idées de promotions IA",
      ],
      faq_h2: "Questions fréquentes",
      need_login: "Connectez-vous pour souscrire à un abonnement.",
      need_claim: "Sélectionnez ou réclamez d'abord votre entreprise dans l'annuaire.",
    },
    auth: {
      h1: "Connexion à votre espace",
      sub: "Connectez-vous avec votre email et mot de passe pour accéder à votre espace partenaire.",
      email_label: "Votre email",
      password_label: "Mot de passe",
      cta: "Se connecter",
      or: "ou",
      no_account: "Pas encore de compte ?",
      create_account: "Créer un compte",
      forgot: "Mot de passe oublié ?",
      verifying: "Connexion en cours…",
      back_to_login: "Retour à la connexion",
      audience_picker_h1: "Se connecter",
      audience_picker_sub: "Choisissez votre profil pour continuer.",
      audience_pro_title: "Je suis commerçant",
      audience_pro_desc: "Réclamez votre fiche, gérez vos photos, avis clients, statistiques et abonnement Premium.",
      audience_pro_cta: "Accéder à mon espace",
      audience_client_title: "Je cherche un commerce",
      audience_client_desc: "Créez un compte pour sauvegarder vos favoris, laisser des avis et recevoir les nouveautés du Haut-Rhin.",
      audience_client_cta: "Continuer comme client",
      browse_free: "Pas besoin de compte pour simplement naviguer —",
      browse_directory: "ouvrez directement l'annuaire",
      change_audience: "Changer de profil",
      space_pro: "Espace commerçant",
      space_client: "Espace client",
      reassure_title: "Connexion rapide et sécurisée",
      reassure_title_2: "avec votre email et mot de passe.",
      terms_consent: "En continuant, vous acceptez les",
      terms: "CGU",
      and: "et la",
      privacy: "politique de confidentialité",
    },
    register: {
      kicker: "Inscription commerçant",
      h1: "Créez votre compte",
      sub: "Remplissez vos informations. Votre compte sera actif immédiatement — pas d'attente de mail.",
      first_name: "Prénom",
      last_name: "Nom",
      business_name: "Nom de votre entreprise",
      business_placeholder: "Ex : Boulangerie Jacques",
      date_of_birth: "Date de naissance",
      email: "Email",
      password: "Mot de passe",
      password_confirm: "Confirmation mot de passe",
      password_min: "min 8 caractères, idéalement majuscule + chiffre + symbole",
      password_match: "Mots de passe identiques.",
      password_mismatch: "Les mots de passe ne correspondent pas.",
      strength_0: "Trop faible",
      strength_1: "Faible",
      strength_2: "Correct",
      strength_3: "Bon",
      strength_4: "Excellent",
      promo: "Code promo (optionnel)",
      accept_terms: "J'accepte les",
      submit: "Créer mon compte",
      submitting: "Création…",
      already: "Déjà un compte ?",
      login_link: "Se connecter",
      welcome: "Bienvenue ! Votre compte est actif.",
    },
    forgot: {
      h1: "Mot de passe oublié",
      sub: "Saisissez votre email, nous vous envoyons un lien pour choisir un nouveau mot de passe.",
      submit: "Envoyer le lien",
      sent_h1: "Email envoyé",
      sent_sub: "Si un compte existe avec cet email, un lien de réinitialisation vient d'être envoyé. Le lien expire dans 1 heure.",
      sent_note: "Le mail peut mettre 1 à 2 minutes à arriver. Pensez à vérifier vos spams.",
      reset_h1: "Nouveau mot de passe",
      reset_sub: "Choisissez votre nouveau mot de passe (minimum 8 caractères).",
      reset_new: "Nouveau mot de passe",
      reset_confirm: "Confirmer le mot de passe",
      reset_submit: "Mettre à jour",
      reset_success: "Mot de passe mis à jour. Connectez-vous.",
    },
    trial: {
      welcome_kicker: "OFFRE DE BIENVENUE · SANS CARTE BANCAIRE",
      welcome_h1: "Bienvenue {name}, démarrez votre essai gratuit 14 jours",
      welcome_sub: "Débloquez toutes les fonctionnalités Premium AI pour transformer votre fiche en machine à clients. Aucun engagement, aucune carte demandée.",
      welcome_cta: "Activer mon essai 14 jours",
      welcome_skip: "Plus tard",
    },
    cookies: {
      title: "Cookies & confidentialité",
      message: "Nous utilisons des cookies essentiels (toujours actifs), et des cookies analytiques + IA avec votre consentement, conformément au RGPD.",
      accept_all: "Tout accepter",
      essential_only: "Essentiels uniquement",
      manage: "Gérer",
    },
    comparator: {
      unlock: "{n} avantages débloqués avec Premium",
      free: "Gratuit",
      premium: "Premium",
      cta: "Passer Premium",
      row_visible: "Fiche visible dans l'annuaire",
      row_basic: "Infos basiques (adresse, SIRET)",
      row_contact: "Contact client direct (mail)",
      row_top: "Mise en tête de l'annuaire (badge doré)",
      row_photos: "Photos illimitées + logo HD",
      row_desc: "Description enrichie (IA)",
      row_clickable: "Téléphone & WhatsApp cliquables",
      row_stats: "Statistiques de visibilité",
      row_reviews: "Réponse aux avis (brouillon IA)",
      row_flyer: "Flyer PDF à imprimer (avec QR)",
      row_report: "Rapport mensuel PDF (KPI + conseils IA)",
      row_hours: "Suggestion meilleures heures (IA)",
      row_shares: "Partages comptabilisés (bouche-à-oreille)",
      visibility_lift: "Premium augmente typiquement la visibilité de +120 à +180%.",
    },
    footer: {
      tagline: "L'annuaire premium des entreprises du Haut-Rhin.",
      legal: "Mentions légales",
      cgu: "CGU",
      cgv: "CGV",
      rgpd: "RGPD",
      cookies: "Cookies",
      about: "À propos",
      contact: "Contact",
    },
    widgets: {
      concierge: "Concierge IA",
      onboarding: "Comment ça marche ?",
    },
    pagination: {
      page: "Page",
      of: "sur",
      results: "résultats",
      to: "à",
      total_of: "sur",
      first: "Première page",
      previous: "Précédent",
      next: "Suivant",
      last: "Dernière page",
      goto_placeholder: "Aller à…",
      goto_aria: "Aller à la page",
      label: "Pagination",
    },
    scarcity: {
      remaining: "Plus que {n} places",
      tooltip: "Seulement {n} places restantes dans l'offre lancement — −50 % la 1ʳᵉ année",
    },
    ticker: {
      new_premium: "Nouveau Premium",
      new_premium_ai: "Nouveau Premium IA",
      view_listing: "Voir leur fiche",
      close: "Fermer",
      time_recent: "récemment",
      time_now: "à l'instant",
      time_min: "il y a {n} min",
      time_hour: "il y a {n} h",
      time_day: "il y a {n} j",
      time_week: "il y a {n} sem.",
    },
    pwa: {
      title: "Installer MonCommerce68",
      description: "Ajoutez l'application sur votre écran d'accueil pour un accès rapide sans navigateur.",
      install_now: "Installer maintenant",
      ios_intro: "Pour installer sur iPhone/iPad :",
      ios_step1_prefix: "Touchez l'icône ",
      ios_step1_bold: "Partager",
      ios_step1_suffix: " en bas de Safari",
      ios_step2_prefix: "Faites défiler et touchez ",
      ios_step2_bold: "« Sur l'écran d'accueil »",
      ios_step3_prefix: "Confirmez en touchant ",
      ios_step3_bold: "Ajouter",
      close: "Fermer",
    },
    share: {
      button: "Partager",
      via: "Partager via",
      copy: "Copier",
      link_copied: "Lien copié, vous pouvez le partager.",
      link_copied_short: "Lien copié.",
      close: "Fermer",
    },
    social_proof: {
      community: "Communauté active",
      joined_one: "{n} commerçant vient de nous rejoindre",
      joined_many: "{n} commerçants viennent de nous rejoindre",
      trust: "{n} commerçants font confiance à MonCommerce68",
      cta: "Rejoignez-les en réclamant votre fiche — c'est gratuit.",
      close: "Fermer",
    },
  },
  en: {
    nav: { directory: "Directory", partner: "Become partner", login: "Sign in", dashboard: "Dashboard", admin: "Admin", logout: "Sign out", search_placeholder: "Search a business…" },
    home: {
      kicker: "The premium Haut-Rhin directory",
      h1: "Become a partner and turn local visibility into real customer opportunities.",
      sub: "Locals across Haut-Rhin — and from just across the border — search for nearby businesses with an AI designed for the local market. Be there when they decide.",
      search_placeholder: "What are you looking for? « emergency plumber Mulhouse », « bakery open now »…",
      search_cta: "Search",
      smart_label: "AI Search",
      proof_users: "Local visitors",
      proof_searches: "Searches performed",
      proof_premium: "Partner businesses",
      proof_cities: "Towns in 68",
      proof_total: "Listed businesses",
      partner_kicker: "For local businesses",
      partner_h2: "A premium listing that works for you, day and night",
      partner_lead: "Priority visibility, direct contact, dedicated AI. No commitment.",
      cta_partner: "See partner offer",
      cta_directory: "Browse directory",
      featured_kicker: "Featured",
      featured_h2: "Our partner businesses",
      shopkeeper_kicker: "Are you a business owner?",
      shopkeeper_h2: "Your business may already be listed",
      shopkeeper_lead: "We have already listed more than {count} businesses in Haut-Rhin from public data. Find yours in seconds, claim it, and put it forward.",
      shopkeeper_find: "Find my business",
      shopkeeper_search_placeholder: "Your business name…",
      shopkeeper_steps_title: "3 steps, that's all",
      shopkeeper_step1: "Search for your business name above",
      shopkeeper_step2: "Click « It's my business » to claim it",
      shopkeeper_step3: "Activate your partner profile to appear first",
    },
    directory: { title: "Haut-Rhin business directory", filter_city: "City", filter_category: "Category", filter_keyword: "Keyword", filter_keyword_placeholder: "Name, city…", all_option: "— All —", results_label: "businesses", premium_only: "Premium partners only", premium_only_chip: "Premium only", premium_new_week: "+{n} this week", premium_new_title: "{n} new Premium businesses this week of {total} total", sort_label: "Sort:", sort_name_asc: "Name A → Z", sort_name_desc: "Name Z → A", sort_city_asc: "City A → Z", sort_postal_asc: "Postal code ↑", sort_premium_first: "Premium first", sort_rating: "Top rated", sort_relevance: "Relevance", view_list: "List", view_map: "Map", loading: "Loading…", empty: "No results yet. Import is running — come back soon.", premium_badge: "Partner", view: "View listing", eyebrow: "Haut-Rhin Directory" },
    business: { claim: "Claim this listing", contact_form: "Send a message", reviews: "Reviews", submit_review: "Leave a review", no_reviews: "No approved review yet.", premium_only_contact: "Direct contact via partner listing — become a partner to enable click-to-call & WhatsApp.", website: "Website", send_message: "Send", name: "Name", email: "Email", phone: "Phone", message: "Your message", sent: "Message sent. You will be contacted shortly." },
    pricing: { kicker: "No commitment, cancel anytime", h1: "Activate the tools that convert local visibility", premium_name: "Premium", premium_ai_name: "Premium AI", per_month: "/ month", per_year: "/ year", billing_monthly: "Monthly", billing_annual: "Yearly", billing_save: "-17%", promo_banner: "-50% off your first year on any new subscription", promo_first_year: "-50% the first year", promo_first_months: "-50% for the first 12 months", price_then: "Then", equivalent_monthly: "i.e. {price} / month", start_trial_cta: "Start my free trial", cta: "Become partner", most_popular: "Most chosen", features_premium: ["Priority visibility", "Partner badge", "Direct call button", "WhatsApp button", "Monthly stats", "Priority email support"], features_premium_ai: ["All in Premium", "AI content generator", "Indicative pricing assistant", "AI review replies", "AI visibility advisor", "AI promotion ideas"], faq_h2: "Frequently asked", need_login: "Sign in to subscribe.", need_claim: "First select or claim your business in the directory." },
    auth: { h1: "Sign in to your account", sub: "Sign in with your email and password to access your partner dashboard.", email_label: "Your email", password_label: "Password", cta: "Sign in", or: "or", no_account: "No account yet?", create_account: "Create an account", forgot: "Forgot password?", verifying: "Signing in…", back_to_login: "Back to sign in", audience_picker_h1: "Sign in", audience_picker_sub: "Pick your profile to continue.", audience_pro_title: "I'm a business owner", audience_pro_desc: "Claim your listing, manage photos, reviews, statistics and your Premium subscription.", audience_pro_cta: "Go to my dashboard", audience_client_title: "I'm looking for a business", audience_client_desc: "Create an account to save favourites, leave reviews and get Haut-Rhin updates.", audience_client_cta: "Continue as customer", browse_free: "No account needed just to browse —", browse_directory: "open the directory directly", change_audience: "Change profile", space_pro: "Partner space", space_client: "Customer space", reassure_title: "Fast and secure sign-in", reassure_title_2: "with your email and password.", terms_consent: "By continuing, you accept the", terms: "Terms", and: "and the", privacy: "privacy policy" },
    register: { kicker: "Business sign-up", h1: "Create your account", sub: "Fill in your details. Your account is active immediately — no email to wait for.", first_name: "First name", last_name: "Last name", business_name: "Your business name", business_placeholder: "Ex: Jacques Bakery", date_of_birth: "Date of birth", email: "Email", password: "Password", password_confirm: "Confirm password", password_min: "min 8 chars, ideally uppercase + digit + symbol", password_match: "Passwords match.", password_mismatch: "Passwords don't match.", strength_0: "Too weak", strength_1: "Weak", strength_2: "Fair", strength_3: "Good", strength_4: "Excellent", promo: "Promo code (optional)", accept_terms: "I accept the", submit: "Create my account", submitting: "Creating…", already: "Already have an account?", login_link: "Sign in", welcome: "Welcome! Your account is active." },
    forgot: { h1: "Forgot password", sub: "Enter your email — we'll send a link to choose a new password.", submit: "Send the link", sent_h1: "Email sent", sent_sub: "If an account exists for this email, a reset link has just been sent. The link expires in 1 hour.", sent_note: "The email may take 1 to 2 minutes to arrive. Don't forget to check spam.", reset_h1: "New password", reset_sub: "Choose your new password (minimum 8 characters).", reset_new: "New password", reset_confirm: "Confirm password", reset_submit: "Update", reset_success: "Password updated. Please sign in." },
    trial: { welcome_kicker: "WELCOME OFFER · NO CREDIT CARD", welcome_h1: "Welcome {name}, start your 14-day free trial", welcome_sub: "Unlock all Premium AI features to turn your listing into a customer machine. No commitment, no card required.", welcome_cta: "Start my 14-day trial", welcome_skip: "Later" },
    cookies: { title: "Cookies & privacy", message: "We use essential cookies (always on), and analytics + AI cookies with your consent, in line with GDPR.", accept_all: "Accept all", essential_only: "Essential only", manage: "Manage" },
    comparator: { unlock: "{n} benefits unlocked with Premium", free: "Free", premium: "Premium", cta: "Go Premium", row_visible: "Listed in the directory", row_basic: "Basic info (address, registration)", row_contact: "Direct customer contact (email)", row_top: "Top of the directory (gold badge)", row_photos: "Unlimited photos + HD logo", row_desc: "Enriched description (AI)", row_clickable: "Clickable phone & WhatsApp", row_stats: "Visibility statistics", row_reviews: "AI-drafted review replies", row_flyer: "Printable PDF flyer (with QR)", row_report: "Monthly PDF report (KPIs + AI tips)", row_hours: "Best hours suggestion (AI)", row_shares: "Shares tracked (word-of-mouth)", visibility_lift: "Premium typically boosts visibility by +120 to +180%." },
    footer: { tagline: "The premium Haut-Rhin business directory.", legal: "Legal notice", cgu: "Terms", cgv: "Sales terms", rgpd: "GDPR", cookies: "Cookies", about: "About", contact: "Contact" },
    widgets: { concierge: "AI Concierge", onboarding: "How does it work?" },
    pagination: {
      page: "Page",
      of: "of",
      results: "results",
      to: "to",
      total_of: "of",
      first: "First page",
      previous: "Previous",
      next: "Next",
      last: "Last page",
      goto_placeholder: "Go to…",
      goto_aria: "Go to page",
      label: "Pagination",
    },
    scarcity: {
      remaining: "Only {n} spots left",
      tooltip: "Only {n} spots left in the launch offer — −50% the 1st year",
    },
    ticker: {
      new_premium: "New Premium",
      new_premium_ai: "New Premium AI",
      view_listing: "View their listing",
      close: "Close",
      time_recent: "recently",
      time_now: "just now",
      time_min: "{n} min ago",
      time_hour: "{n}h ago",
      time_day: "{n}d ago",
      time_week: "{n}w ago",
    },
    pwa: {
      title: "Install MonCommerce68",
      description: "Add the app to your home screen for fast access without a browser.",
      install_now: "Install now",
      ios_intro: "To install on iPhone/iPad:",
      ios_step1_prefix: "Tap the ",
      ios_step1_bold: "Share",
      ios_step1_suffix: " icon at the bottom of Safari",
      ios_step2_prefix: "Scroll and tap ",
      ios_step2_bold: "« Add to Home Screen »",
      ios_step3_prefix: "Confirm by tapping ",
      ios_step3_bold: "Add",
      close: "Close",
    },
    share: {
      button: "Share",
      via: "Share via",
      copy: "Copy",
      link_copied: "Link copied, you can share it.",
      link_copied_short: "Link copied.",
      close: "Close",
    },
    social_proof: {
      community: "Active community",
      joined_one: "{n} business owner just joined us",
      joined_many: "{n} business owners just joined us",
      trust: "{n} businesses trust MonCommerce68",
      cta: "Join them — claim your listing for free.",
      close: "Close",
    },
  },
  de: {
    nav: { directory: "Verzeichnis", partner: "Partner werden", login: "Anmelden", dashboard: "Partnerbereich", admin: "Admin", logout: "Abmelden", search_placeholder: "Unternehmen suchen…" },
    home: {
      kicker: "Das Premium-Verzeichnis im Haut-Rhin",
      h1: "Werden Sie Partner und verwandeln Sie lokale Sichtbarkeit in echte Kundenanfragen.",
      sub: "Einwohner im Haut-Rhin — und über die Grenzen hinaus — suchen lokale Unternehmen mit einer KI, die fürs Lokale gemacht ist. Seien Sie da, wenn sie entscheiden.",
      search_placeholder: "Wonach suchen Sie? « Notdienst Klempner Mulhouse », « Bäckerei geöffnet »…",
      search_cta: "Suchen",
      smart_label: "KI-Suche",
      proof_users: "Lokale Besucher",
      proof_searches: "Durchgeführte Suchen",
      proof_premium: "Partner-Unternehmen",
      proof_cities: "Gemeinden im Haut-Rhin",
      proof_total: "Gelistete Unternehmen",
      partner_kicker: "Für Unternehmer",
      partner_h2: "Ein Premium-Eintrag, der Tag und Nacht für Sie arbeitet",
      partner_lead: "Vorrangige Sichtbarkeit, direkter Kontakt, dedizierte KI. Ohne Bindung.",
      cta_partner: "Partnerangebot ansehen",
      cta_directory: "Verzeichnis öffnen",
      featured_kicker: "Hervorgehoben",
      featured_h2: "Unsere Partnerunternehmen",
      shopkeeper_kicker: "Sind Sie Unternehmer?",
      shopkeeper_h2: "Ihr Unternehmen ist möglicherweise bereits gelistet",
      shopkeeper_lead: "Wir haben bereits über {count} Unternehmen im Haut-Rhin aus öffentlichen Daten erfasst. Finden Sie Ihres in Sekunden, beanspruchen Sie es und präsentieren Sie es optimal.",
      shopkeeper_find: "Mein Unternehmen finden",
      shopkeeper_search_placeholder: "Name Ihres Unternehmens…",
      shopkeeper_steps_title: "3 Schritte, das war's",
      shopkeeper_step1: "Suchen Sie oben nach Ihrem Unternehmensnamen",
      shopkeeper_step2: "Klicken Sie auf « Das ist mein Unternehmen », um es zu beanspruchen",
      shopkeeper_step3: "Aktivieren Sie Ihr Partnerprofil, um an erster Stelle zu erscheinen",
    },
    directory: { title: "Unternehmensverzeichnis Haut-Rhin", filter_city: "Stadt", filter_category: "Kategorie", filter_keyword: "Stichwort", filter_keyword_placeholder: "Name, Stadt…", all_option: "— Alle —", results_label: "Unternehmen", premium_only: "Nur Premium-Partner", premium_only_chip: "Nur Premium", premium_new_week: "+{n} diese Woche", premium_new_title: "{n} neue Premium-Unternehmen diese Woche von insgesamt {total}", sort_label: "Sortieren:", sort_name_asc: "Name A → Z", sort_name_desc: "Name Z → A", sort_city_asc: "Stadt A → Z", sort_postal_asc: "PLZ ↑", sort_premium_first: "Premium zuerst", sort_rating: "Beste Bewertungen", sort_relevance: "Relevanz", view_list: "Liste", view_map: "Karte", loading: "Lädt…", empty: "Noch keine Ergebnisse. Import läuft — schauen Sie bald vorbei.", premium_badge: "Partner", view: "Eintrag ansehen", eyebrow: "Verzeichnis Haut-Rhin" },
    business: { claim: "Eintrag beanspruchen", contact_form: "Nachricht senden", reviews: "Bewertungen", submit_review: "Bewertung abgeben", no_reviews: "Noch keine genehmigten Bewertungen.", premium_only_contact: "Direktkontakt über Partner-Eintrag — werden Sie Partner, um Klick-zu-Anrufen & WhatsApp zu aktivieren.", website: "Webseite", send_message: "Senden", name: "Name", email: "E-Mail", phone: "Telefon", message: "Ihre Nachricht", sent: "Nachricht gesendet. Sie werden in Kürze kontaktiert." },
    pricing: { kicker: "Ohne Bindung, jederzeit kündbar", h1: "Aktivieren Sie die Tools, die lokale Sichtbarkeit in Kunden verwandeln", premium_name: "Premium", premium_ai_name: "Premium KI", per_month: "/ Monat", per_year: "/ Jahr", billing_monthly: "Monatlich", billing_annual: "Jährlich", billing_save: "-17%", promo_banner: "-50 % im 1. Jahr für alle neuen Abonnements", promo_first_year: "-50 % im 1. Jahr", promo_first_months: "-50 % in den ersten 12 Monaten", price_then: "Danach", equivalent_monthly: "entspricht {price} / Monat", start_trial_cta: "Kostenlose Testphase starten", cta: "Partner werden", most_popular: "AM BELIEBTESTEN", features_premium: ["Vorrangige Sichtbarkeit", "Partner-Abzeichen", "Direktanruf-Button", "WhatsApp-Button", "Monatsstatistiken", "Vorrangiger E-Mail-Support"], features_premium_ai: ["Alles aus Premium", "KI-Content-Generator", "KI-Preisberater", "KI-Bewertungsantworten", "KI-Sichtbarkeitsberater", "KI-Aktionsideen"], faq_h2: "Häufige Fragen", need_login: "Bitte anmelden, um zu abonnieren.", need_claim: "Wählen oder beanspruchen Sie zuerst Ihr Unternehmen im Verzeichnis." },
    auth: { h1: "Anmeldung zu Ihrem Partnerbereich", sub: "Melden Sie sich mit Ihrer E-Mail-Adresse und Ihrem Passwort an, um auf Ihr Partner-Dashboard zuzugreifen.", email_label: "Ihre E-Mail", password_label: "Passwort", cta: "Anmelden", or: "oder", no_account: "Noch kein Konto?", create_account: "Konto erstellen", forgot: "Passwort vergessen?", verifying: "Anmeldung läuft…", back_to_login: "Zurück zur Anmeldung", audience_picker_h1: "Anmelden", audience_picker_sub: "Wählen Sie Ihr Profil.", audience_pro_title: "Ich bin Unternehmer", audience_pro_desc: "Beanspruchen Sie Ihren Eintrag, verwalten Sie Fotos, Bewertungen, Statistiken und Ihr Premium-Abo.", audience_pro_cta: "Zum Dashboard", audience_client_title: "Ich suche ein Geschäft", audience_client_desc: "Erstellen Sie ein Konto, um Favoriten zu speichern, Bewertungen abzugeben und Haut-Rhin-Neuigkeiten zu erhalten.", audience_client_cta: "Als Kunde fortfahren", browse_free: "Zum Stöbern kein Konto nötig —", browse_directory: "Verzeichnis direkt öffnen", change_audience: "Profil wechseln", space_pro: "Partnerbereich", space_client: "Kundenbereich", reassure_title: "Schnelle und sichere Anmeldung", reassure_title_2: "mit E-Mail und Passwort.", terms_consent: "Mit der Fortsetzung akzeptieren Sie die", terms: "AGB", and: "und die", privacy: "Datenschutzrichtlinie" },
    register: { kicker: "Registrierung Unternehmer", h1: "Konto erstellen", sub: "Füllen Sie Ihre Daten aus. Ihr Konto ist sofort aktiv — keine E-Mail-Wartezeit.", first_name: "Vorname", last_name: "Nachname", business_name: "Name Ihres Unternehmens", business_placeholder: "Z. B.: Bäckerei Jacques", date_of_birth: "Geburtsdatum", email: "E-Mail", password: "Passwort", password_confirm: "Passwort bestätigen", password_min: "mind. 8 Zeichen, idealerweise Großbuchstabe + Zahl + Symbol", password_match: "Passwörter stimmen überein.", password_mismatch: "Passwörter stimmen nicht überein.", strength_0: "Zu schwach", strength_1: "Schwach", strength_2: "OK", strength_3: "Gut", strength_4: "Exzellent", promo: "Promo-Code (optional)", accept_terms: "Ich akzeptiere die", submit: "Konto erstellen", submitting: "Erstelle…", already: "Bereits ein Konto?", login_link: "Anmelden", welcome: "Willkommen! Ihr Konto ist aktiv." },
    forgot: { h1: "Passwort vergessen", sub: "Geben Sie Ihre E-Mail ein — wir senden einen Link zum Passwort-Reset.", submit: "Link senden", sent_h1: "E-Mail gesendet", sent_sub: "Wenn ein Konto mit dieser E-Mail existiert, wurde ein Reset-Link gesendet. Der Link läuft nach 1 Stunde ab.", sent_note: "Die E-Mail kann 1 bis 2 Minuten benötigen. Prüfen Sie auch den Spam-Ordner.", reset_h1: "Neues Passwort", reset_sub: "Wählen Sie Ihr neues Passwort (mindestens 8 Zeichen).", reset_new: "Neues Passwort", reset_confirm: "Passwort bestätigen", reset_submit: "Aktualisieren", reset_success: "Passwort aktualisiert. Bitte anmelden." },
    trial: { welcome_kicker: "WILLKOMMENSANGEBOT · OHNE KREDITKARTE", welcome_h1: "Willkommen {name}, starten Sie Ihre 14-tägige Testphase", welcome_sub: "Schalten Sie alle Premium-AI-Funktionen frei und machen Sie aus Ihrem Eintrag eine Kundenmaschine. Keine Bindung, keine Karte erforderlich.", welcome_cta: "14-Tage-Testphase starten", welcome_skip: "Später" },
    cookies: { title: "Cookies & Datenschutz", message: "Wir verwenden essentielle Cookies (immer aktiv) und Analyse- + KI-Cookies mit Ihrer Einwilligung, gemäß DSGVO.", accept_all: "Alle akzeptieren", essential_only: "Nur essentielle", manage: "Verwalten" },
    comparator: { unlock: "{n} Vorteile mit Premium freigeschaltet", free: "Kostenlos", premium: "Premium", cta: "Premium werden", row_visible: "Eintrag im Verzeichnis sichtbar", row_basic: "Basisdaten (Adresse, SIRET)", row_contact: "Direkter Kundenkontakt (E-Mail)", row_top: "Ganz oben im Verzeichnis (Goldabzeichen)", row_photos: "Unbegrenzte Fotos + HD-Logo", row_desc: "Erweiterte Beschreibung (KI)", row_clickable: "Telefon & WhatsApp klickbar", row_stats: "Sichtbarkeitsstatistiken", row_reviews: "KI-Antwortentwürfe für Bewertungen", row_flyer: "Druckbares PDF-Flyer (mit QR-Code)", row_report: "Monatlicher PDF-Bericht (KPIs + KI-Tipps)", row_hours: "Vorschläge der besten Zeiten (KI)", row_shares: "Geteilte Einträge gezählt (Mundpropaganda)", visibility_lift: "Premium steigert die Sichtbarkeit typischerweise um +120 bis +180 %." },
    footer: { tagline: "Das Premium-Verzeichnis der Haut-Rhin-Unternehmen.", legal: "Impressum", cgu: "AGB", cgv: "Verkaufsbedingungen", rgpd: "DSGVO", cookies: "Cookies", about: "Über", contact: "Kontakt" },
    widgets: { concierge: "KI-Concierge", onboarding: "Wie funktioniert es?" },
    pagination: {
      page: "Seite",
      of: "von",
      results: "Ergebnisse",
      to: "bis",
      total_of: "von",
      first: "Erste Seite",
      previous: "Zurück",
      next: "Weiter",
      last: "Letzte Seite",
      goto_placeholder: "Gehe zu…",
      goto_aria: "Zur Seite gehen",
      label: "Seitennavigation",
    },
    scarcity: {
      remaining: "Nur noch {n} Plätze",
      tooltip: "Nur noch {n} Plätze im Launch-Angebot — −50 % im 1. Jahr",
    },
    ticker: {
      new_premium: "Neu Premium",
      new_premium_ai: "Neu Premium KI",
      view_listing: "Eintrag ansehen",
      close: "Schließen",
      time_recent: "kürzlich",
      time_now: "gerade eben",
      time_min: "vor {n} Min.",
      time_hour: "vor {n} Std.",
      time_day: "vor {n} T.",
      time_week: "vor {n} Wo.",
    },
    pwa: {
      title: "MonCommerce68 installieren",
      description: "Fügen Sie die App zu Ihrem Startbildschirm hinzu — schneller Zugriff ohne Browser.",
      install_now: "Jetzt installieren",
      ios_intro: "Installation auf iPhone/iPad:",
      ios_step1_prefix: "Tippen Sie auf das ",
      ios_step1_bold: "Teilen",
      ios_step1_suffix: "-Symbol unten in Safari",
      ios_step2_prefix: "Scrollen Sie und tippen Sie auf ",
      ios_step2_bold: "« Zum Home-Bildschirm »",
      ios_step3_prefix: "Bestätigen Sie mit ",
      ios_step3_bold: "Hinzufügen",
      close: "Schließen",
    },
    share: {
      button: "Teilen",
      via: "Teilen über",
      copy: "Kopieren",
      link_copied: "Link kopiert, Sie können ihn jetzt teilen.",
      link_copied_short: "Link kopiert.",
      close: "Schließen",
    },
    social_proof: {
      community: "Aktive Community",
      joined_one: "{n} Unternehmer ist gerade beigetreten",
      joined_many: "{n} Unternehmer sind gerade beigetreten",
      trust: "{n} Unternehmen vertrauen MonCommerce68",
      cta: "Schließen Sie sich an — beanspruchen Sie kostenlos Ihren Eintrag.",
      close: "Schließen",
    },
  },
};

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const detect = () => {
    // French-first app (Haut-Rhin). Only honour an explicit user choice
    // (stored in localStorage from the FR/EN/DE picker). Never auto-pick EN
    // from navigator.language — that silently flipped anonymous visitors
    // to English, which is wrong for our core audience.
    if (typeof window === "undefined") return "fr";
    const stored = localStorage.getItem("mc68_lang");
    if (stored && DICT[stored]) return stored;
    return "fr";
  };
  const [lang, setLang] = useState(detect);
  useEffect(() => {
    localStorage.setItem("mc68_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);
  const value = useMemo(() => ({
    lang,
    setLang,
    t: DICT[lang] || DICT.fr,
  }), [lang]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}
